/** @file clip.copyPose: copies the (interpolated) pose at one frame, possibly of another clip, to a frame. */
import { checkArray } from '@zilo/pose3d-format/validate/fields/checkArray';
import { checkString } from '@zilo/pose3d-format/validate/fields/checkString';
import type { CommandDef } from '../apply/commandDef';
import { samplePose } from '../pose/samplePose';
import { findClip } from '../puppetDoc/findClip';
import { compact } from '../validate/compact';
import { readInput } from '../validate/readInput';
import { keyedNodes } from './keyedNodes';
import { writePose } from './writePose';

/**
 * `clip.copyPose { clip, from, to, fromClip?, nodes? }` → `{ frame: to }`: samples `nodes` (default every node the
 * source clip keys) at `from` and merges them into the key at `to`. Inverse: the previous key at `to`.
 */
export const clipCopyPoseCommand: CommandDef<'clip.copyPose'> = {
  name: 'clip.copyPose',
  /** Checks the raw input (VALIDATION under the input path). */
  validate: (input, path) => {
    const r = readInput(input, path, ['clip', 'from', 'to', 'fromClip', 'nodes']);
    return compact({
      clip: r.str('clip'), from: r.int('from', { min: 0 }), to: r.int('to', { min: 0 }), fromClip: r.opt('fromClip', r.str),
      nodes: r.opt('nodes', (k) => r.check(k, checkArray(checkString())) as string[]),
    });
  },
  /** Applies the command to the draft and returns its result and inverse. */
  apply: (draft, input) => {
    const { name, clip } = findClip(draft, input.clip);
    const src = input.fromClip === undefined ? clip : findClip(draft, input.fromClip, '/input/fromClip').clip;
    const pose = samplePose(src, input.from, input.nodes ?? keyedNodes(src));
    return writePose(draft, { clipName: name, frame: input.to, nodes: pose, merge: true });
  },
};
